import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';

import { Paper, Container, Grid, Typography, Hidden, IconButton, Tooltip } from '@material-ui/core';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import Select from '../Select';
import Datepicker from '../Datepicker';
import Breadcrumb from '../Breadcrumb';
import Autocomplete from '../Autocomplete';
import Button from 'components/Button';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';

import { setGraphicTimeRange, clearSearch } from '../Filter/store/actions';
import { setEstablishmentId, setEstablishmentTypes } from '../../pages/BusinessInfo/store/actions';
import { setCalendarOption, setIsCalendarSelected, setIsMapSelected, setCurrentDateCycle } from 'pages/Scheduling/store/actions';
import { loadCategories } from '../../pages/NotificationsConfig/store/thunk';
import { setCurrentNotificationCategory } from '../../pages/NotificationsConfig/store/actions';
import { changeDate } from 'components/LayoutRoute/store/actions';

import { convertToEstablishmentName } from '../../helpers/converters';
import { timeRanges } from './resources';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

import { useStyles } from './styles';

const Subheader = ({
  name,
  path,
  previousPaths,
  showDate,
  showTimeRange,
  hideEstablishments,
  filterEstablishments,
  showEstablishmentName
}) => {
  const dispatch = useDispatch();

  const classes = useStyles();

  const history = useHistory();

  const { width } = useWindowDimensions();

  const md = showDate || showTimeRange ? 4 : 8;
  const isSchedulingXs = width < 960;

  const isDashboard = history.location.pathname == '/visao-geral';
  const isNotificationConfig = history.location.pathname == '/configuracao-de-lembrete';
  const isOperationalHistory = history.location.pathname == '/historico-operacional';
  const isClientList = history.location.pathname == '/clientes';
  const isScheduling = history.location.pathname == '/agendamento';
  const isTransactions = history.location.pathname == '/transacoes';

  const defaultEstablishmentId = useSelector(state => state.login.establishments)[1]?.establishmentId;
  const { establishmentId } = useSelector(state => state.businessInfo);

  const { userId } = useSelector(state => state.profile);
  const { establishments } = useSelector(state => state.login);
  const { graphicTimeRange } = useSelector(state => state.filters);

  const [selectCustomGraphicTimeRange, setSelectCustomGraphicTimeRange] = useState(graphicTimeRange == 'custom' ? true : false);

  const schedulingOptions = useSelector(state => state.scheduling.calendarOptions);
  const selectedSchedulingOption = useSelector(state => state.scheduling.selectedCalendarOption);
  const { isCalendarSelected, isMapSelected, dateCycleText } = useSelector(state => state.scheduling);

  const currentNotificationCategory = useSelector(state => state.notificationsConfig.currentCategory);

  const notificationsTypeList = useSelector(state => state.notificationsConfig.categories).map(notifications => {
    return {
      value: notifications.id,
      label: notifications.name
    };
  });

  const selectedNotificationType =
    notificationsTypeList?.find(notificationsType => notificationsType.value == 1)?.value;

  useEffect(() => {
    const selectedEstablishmentId = establishmentId || defaultEstablishmentId;

    const params = {
      userId,
      establishmentId: selectedEstablishmentId,
      page: 1
    }

    if(isNotificationConfig) {
      dispatch(loadCategories(params));
    }
  }, [establishmentId, isNotificationConfig]);

  useEffect(() => {
    setSelectCustomGraphicTimeRange(graphicTimeRange == 'custom');
  }, [graphicTimeRange]);

  useEffect(() => {
    if(graphicTimeRange == 'day') {
      const date = moment(moment.now()).format('YYYY-MM-DD');
      dispatch(changeDate(date, date));
    }

    if(graphicTimeRange == 'yesterday') {
      const date = moment(moment.now()).subtract(1, 'days').format('YYYY-MM-DD');
      dispatch(changeDate(date, date));
    }

    if(graphicTimeRange == 'week') {
      const startDate = moment(moment.now()).subtract(7, 'days').format('YYYY-MM-DD');
      const endDate = moment(moment.now()).format('YYYY-MM-DD');
      dispatch(changeDate(startDate, endDate));
    }

    if(graphicTimeRange == 'month') {
      const startDate = moment(moment.now()).subtract(30, 'days').format('YYYY-MM-DD');
      const endDate = moment(moment.now()).format('YYYY-MM-DD');
      dispatch(changeDate(startDate, endDate));
    }
  }, [graphicTimeRange]);

  const handleChangeEstablishment = (e, option) => {
    const establishmentId = option.value;

    dispatch(setEstablishmentTypes(option?.establishmentTypes));
    dispatch(setEstablishmentId({ establishmentId }));

    if(!isClientList) {
      dispatch(clearSearch());
      dispatch(setGraphicTimeRange(graphicTimeRange));
    }
  }

  const handleChangeSelectedCategory = category => {
    dispatch(setCurrentNotificationCategory(category));
  }

  const handleGraphicTimeRange = event => {
    setSelectCustomGraphicTimeRange(false);
    dispatch(setGraphicTimeRange(event.target.value));

    if(event.target.value == 'custom') {
      setSelectCustomGraphicTimeRange(true);
    }
  }

  if(isScheduling) {
    return(
      <Paper
        style={{
          backgroundColor: '#F1F4F9',
          minHeight: 72,
          paddingTop: 8,
          paddingBottom: isSchedulingXs ? 12 : 8
        }}
      >
        <Grid
          container
          alignItems='center'
          style={{
            paddingRight: 10,
            paddingLeft: 10,
            rowGap: isSchedulingXs ? 10 : 0
          }}
        >
          <Grid container xs={12} md={3} lg={3} justify='center'>
            <Breadcrumb
              mainPath={{ name, path }}
              previousPaths={previousPaths}
            />
          </Grid>
          <Grid container xs={12} md={3} lg={4} justify='center'>
            <Autocomplete
              value={establishmentId}
              options={filterEstablishments}
              onChange={(e, opt) => {
                handleChangeEstablishment(e, opt);
              }}
            />
          </Grid>
          <Grid container xs={12} md={6} lg={5} justify='flex-end'>
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: isSchedulingXs ? 'column' : 'row',
                justifyContent: 'flex-end',
                alignItems: isSchedulingXs ? 'stretch' : 'center',
                gap: isSchedulingXs ? 10 : 20
              }}
            >
              {!isMapSelected && (
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isSchedulingXs ? 'space-between' : 'flex-end',
                    flexWrap: isSchedulingXs ? 'wrap' : 'nowrap',
                    gap: 8
                  }}
                >
                  {isCalendarSelected && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isSchedulingXs ? 'space-between' : 'flex-start',
                        width: isSchedulingXs ? '100%' : 'auto',
                        gap: 10
                      }}
                    >
                      <IconButton
                        style={{ height: 30, width: 30, backgroundColor: 'rgba(21, 56, 72, 0.2)', color: '#022A5C' }}
                        onClick={() => dispatch(setCurrentDateCycle('prev'))}
                      >
                        <KeyboardArrowLeft />
                      </IconButton>
                      <Typography color='primary' style={{ whiteSpace: 'nowrap' }}>
                        {dateCycleText}
                      </Typography>
                      <IconButton
                        style={{ height: 30, width: 30, backgroundColor: 'rgba(21, 56, 72, 0.2)', color: '#022A5C' }}
                        onClick={() => dispatch(setCurrentDateCycle('next'))}
                      >
                        <KeyboardArrowRight />
                      </IconButton>
                    </div>
                  )}
                  {isCalendarSelected && (
                    <div style={{ width: isSchedulingXs ? '100%' : '100px' }}>
                      <Select
                        options={schedulingOptions}
                        value={selectedSchedulingOption}
                        onChange={event => dispatch(setCalendarOption(event.target.value))}
                      />
                    </div>
                  )}
                </div>
              )}
              {isMapSelected && (
                <div style={{ width: isSchedulingXs ? '100%' : '60%' }}>
                  <Datepicker />
                </div>
              )}
              <Tooltip
                placement='top'
                title='Configuração de Agendamento'
                enterTouchDelay={0}
              >
                <IconButton
                  style={{
                    width: 'fit-content',
                    alignSelf: isSchedulingXs ? 'flex-end' : 'center'
                  }}
                  onClick={() => history.push('/configuracao-agendamento')}
                >
                  <FontAwesomeIcon
                    icon={faCog}
                    color="#757575"
                    size="md"
                  />
                </IconButton>
              </Tooltip>
              <Button
                type="button"
                color="primary"
                onClick={() => {
                  dispatch(setIsCalendarSelected(true));
                  dispatch(setIsMapSelected(false));
                  dispatch(setCurrentDateCycle('today'));
                }}
              >
                Hoje
              </Button>
            </div>
          </Grid>
        </Grid>
      </Paper>
    );
  }

  return(
    <Paper square elevation={2} className={classes.root}>
      <Container maxWidth={isScheduling ? false : 'lg'}>
        <Grid container>
          <Grid item xs={12}>
            <Grid container>
              {isScheduling && (
                <Grid item lg={3} xs={0} />
              )}
              <Grid
                lg={isScheduling ? 6 : 12}
                md={isScheduling ? 7 : 12}
                xs={12}
                alignItems="center"
                justify={isNotificationConfig || isScheduling || selectCustomGraphicTimeRange ? "space-between" : "flex-end"}
              >
                <Grid
                  container
                  justify={isScheduling ? 'space-between' : 'center'}
                  alignItems='center'
                >
                  {!selectCustomGraphicTimeRange && (
                    <Grid
                      item
                      lg={isScheduling ? 5 : isNotificationConfig ? 4 : md}
                      md={isScheduling ? 5 : (showDate || showTimeRange) || isNotificationConfig ? 4 : 6}
                      xs={12}
                      mdDown={!!showTimeRange}
                      implementation="css"
                      component={Hidden}
                    >
                      <Breadcrumb
                        mainPath={{ name, path }}
                        previousPaths={previousPaths}
                      />
                    </Grid>
                  )}
                  {hideEstablishments || (
                    <Grid
                      item
                      lg={isScheduling ? 7 : 4}
                      md={isScheduling ? 7 : showDate || isNotificationConfig || selectCustomGraphicTimeRange ? 4 : 6}
                      xs={12}
                    >
                      <Autocomplete
                        value={establishmentId}
                        options={filterEstablishments}
                        onChange={(e, opt) => handleChangeEstablishment(e, opt)}
                      />
                    </Grid>
                  )}
                  {isNotificationConfig && (
                    <Grid
                      item
                      md={2}
                      xs={12}
                      style={{ padding: 10 }}
                    >
                      <Select
                        disabled
                        options={notificationsTypeList}
                        value={currentNotificationCategory || selectedNotificationType}
                        onChange={event => handleChangeSelectedCategory(event.target.value)}
                      />
                    </Grid>
                  )}
                  {showDate && (
                    <Grid
                      item
                      lg={4}
                      md={4}
                      xs={12}
                      style={{ paddingLeft: 5 }}
                    >
                      <Grid container className={classes.dateContent}>
                        <Datepicker
                          blockFutureDates={isOperationalHistory}
                          onBlockedDate={() => toast.error('A data selecionada não pode ser maior do que a data atual.')}
                        />
                      </Grid>
                    </Grid>
                  )}
                  {showTimeRange && (
                    <>
                      <Grid
                        item
                        mdDown
                        xs={1}
                        implementation="css"
                        component={Hidden}
                      />
                      <Grid
                        container
                        justifyContent="flex-end"
                        lg={selectCustomGraphicTimeRange ? 6 : 3}
                        md={selectCustomGraphicTimeRange ? 6 : 3}
                        xs={12}
                      >
                        {selectCustomGraphicTimeRange && (
                          <Grid
                            item
                            xs={12}
                            md={9}
                            style={{ paddingRight: 2.5 }}
                          >
                            <Datepicker
                              style={{ marginBottom: 10 }}
                              blockFutureDates={!isTransactions}
                              readOnly={isDashboard}
                              onBlockedDate={() => toast.error('A data selecionada não pode ser maior do que a data atual.')}
                            />
                          </Grid>
                        )}
                        <Grid
                          item
                          xs={12}
                          md={selectCustomGraphicTimeRange ? 3 : 12}
                        >
                          <Select
                            displayEmpty
                            name="graphicTimeRange"
                            options={timeRanges}
                            value={graphicTimeRange}
                            onChange={event => handleGraphicTimeRange(event)}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                  {showEstablishmentName ? (
                    <Grid item lg={4} md={6} xs={12}>
                      <Typography color="primary">
                        <span style={{ fontWeight: 500 }}>Estabelecimento:</span>{' '}
                        {convertToEstablishmentName(establishmentId, establishments)}
                      </Typography>
                    </Grid>
                  ) : (
                    <Grid item lg={4} md={6} xs={12} />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Paper>
  );
};

Subheader.defaultProps = {
  name: '',
  path: '',
  previousPaths: [],
  enableSelectFutureDates: false,
  showDate: false,
  showEstablishmentName: false,
  showTimeRange: false,
  hideEstablishments: false
};

Subheader.propTypes = {
  name: PropTypes.string,
  path: PropTypes.string,
  previousPaths: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      path: PropTypes.string.isRequired
    })
  ),
  filterEstablishments: PropTypes.arrayOf.isRequired,
  enableSelectFutureDates: PropTypes.bool,
  showDate: PropTypes.bool,
  showEstablishmentName: PropTypes.bool,
  showTimeRange: PropTypes.bool,
  hideEstablishments: PropTypes.bool
};

export default Subheader;